import { FC, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogClose,
} from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';
import DoorIcon from '@/components/icons/DoorIcon';
import InvoiceIcon from '@/components/icons/InvoiceIcon';
import MoneyIcon from '@/components/icons/MoneyIcon';
import ClockIcon from '@/components/icons/ClockIcon';
import TransactionDetailsElement from '@/features/transactions/components/TransactionDetailsElement';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { TransactionsUtils } from '@/features/transactions/utils/transactionsUtils';
import { useTransactionDetailsApi } from '@/features/transactions/hooks/useTransactionDetailsApi';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { cn } from '@/lib/utils';
import CurrencyValue from '@/features/finances/components/CurrencyValue';
import { TransactionType } from '../types/transactions-dto';
import { CurrencyAssetType } from '@/utils/CurrencyHelper';

type Props = {
    id: string;
    onClose: () => void;
    isMobile?: boolean;
};

const TransactionDetails: FC<Props> = ({ id, onClose, isMobile }) => {
    const [isOpen, setIsOpen] = useState(true);
    const { t } = useTranslation();

    const { transactionDetailsData } = useTransactionDetailsApi({ id });

    if (!transactionDetailsData) return null;

    const {
        label: transactionLabel,
        icon: TransactionIcon,
        iconStyles: transactionIconStyles,
    } = TransactionsUtils.getTypeConfig(transactionDetailsData?.type);

    const {
        label: feeLabel,
        icon: FeeIcon,
        iconStyles: feeIconStyles,
    } = TransactionsUtils.getFeeConfig(transactionDetailsData?.sign);

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(onClose, 300);
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogDescription />
                    <DialogTitle>
                        {t('main.transactions.components.details.title')}
                        <DialogClose
                            className={cn(
                                'ml-auto flex items-center justify-center cursor-pointer',
                                isMobile && 'size-11 border border-popover-100 rounded-full',
                            )}
                        >
                            <Cancel_01_Icon />
                        </DialogClose>
                    </DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-6 overflow-y-auto overflow-x-hidden select-none">
                    <div className="flex flex-col gap-2">
                        <h4 className="text-sm text-popover-800">
                            {t('main.transactions.components.details.list.fee.title')}
                        </h4>
                        <span className="text-xl font-medium">
                            <CurrencyValue
                                value={transactionDetailsData?.amount}
                                asset={TransactionType[transactionDetailsData.type].toLowerCase() as CurrencyAssetType}
                                addStyles={true}
                            />
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <div className="p-3 rounded-semi-xl flex items-center gap-2 bg-popover-50 text-sm">
                            <TransactionIcon className={transactionIconStyles} />
                            <span>{t(`${transactionLabel}`)}</span>
                        </div>
                        <div className="p-3 rounded-semi-xl flex items-center gap-2 bg-popover-50 text-sm">
                            <FeeIcon className={feeIconStyles} />
                            <span>{t(`${feeLabel}`)}</span>
                        </div>
                    </div>
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-2 text-sm">
                            <h5 className="text-popover-800">
                                {t('main.transactions.components.details.list.general.title')}
                            </h5>
                            <div>
                                <TransactionDetailsElement
                                    icon={DoorIcon}
                                    label={t(
                                        'main.transactions.components.details.list.general.elements.apartment',
                                    )}
                                    value={transactionDetailsData.apartmentName}
                                />
                                <TransactionDetailsElement
                                    icon={InvoiceIcon}
                                    label={t(
                                        'main.transactions.components.details.list.general.elements.description',
                                    )}
                                    value={transactionDetailsData.serviceName}
                                />
                                <TransactionDetailsElement
                                    icon={MoneyIcon}
                                    label={t(
                                        'main.transactions.components.details.list.general.elements.amount',
                                    )}
                                    value={
                                        <CurrencyValue
                                            value={transactionDetailsData?.amount}
                                            asset={TransactionType[
                                                transactionDetailsData.type
                                            ].toLowerCase() as CurrencyAssetType}
                                            addStyles={true}
                                        />
                                    }
                                />
                            </div>
                        </div>
                        <div className="flex flex-col gap-2 text-sm">
                            <h5 className="text-popover-800">
                                {t('main.transactions.components.details.list.other.title')}
                            </h5>
                            <div>
                                <TransactionDetailsElement
                                    icon={ClockIcon}
                                    label={t(
                                        'main.transactions.components.details.list.other.elements.timestamp',
                                    )}
                                    value={DateTimeHelper.formatDate(
                                        transactionDetailsData.date,
                                        3,
                                    )}
                                />
                                <TransactionDetailsElement
                                    icon={InvoiceIcon}
                                    label={t(
                                        'main.transactions.components.details.list.other.elements.transactionID',
                                    )}
                                    value={transactionDetailsData.transactionId}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default TransactionDetails;
